// Work by area (docs/map-work-plan.md): which open operations are one kind of seasonal work (bottom
// paint, batteries out / in, anodes, shrinkwrap, winterize, commission) and how they group, so the
// boats needing the same can of paint or the same supplies can be worked in one run. Pure (no
// Firebase): tests/unit/workRuns.test.mjs runs it. Used by the Yard Map's layers and Work mode.
//
// Op_Type is no guide here (REMOVE BATTERIES is filed as Mechanical, a bottom paint op as "Spring
// Commissioning"), so an op is read by its own name: the op code's title at the start of its
// Description ("REMOVE BATTERIES (6601): …", "BOTTOM PAINT, STORAGE : …"). Short_Description is often
// the work order's title ("Haul, bottom paint, replace anodes" on every op of it), so it's only a
// fallback when there's no Description.
import { isShrinkwrapInstall, planWrap } from './shrinkwrapCore.js';
import { normalizeRules, catalogRowFor, paintNeed } from './seasonalOrderCore.js';
import { anodesOf, anodeNeeds, mainMaterial } from './anodeCore.js';

export const WORK_KINDS = [
  { key: 'paint', label: 'Bottom paint' },
  { key: 'batteries', label: 'Batteries out / in' },
  { key: 'anodes', label: 'Anodes' },
  { key: 'shrinkwrap', label: 'Shrinkwrap' },
  { key: 'winterize', label: 'Winterize engines' },
  { key: 'commission', label: 'Commission engines' },
];
export const KIND_LABEL = Object.fromEntries(WORK_KINDS.map(k => [k.key, k.label]));

// Finished, billed, turned down or not yet approved: not work to send a tech to.
const NOT_OPEN = new Set(['ready to bill', 'complete', 'billed', 'closed', 'declined', 'awaiting approval']);
export const isOpenOp = op => !!op && !NOT_OPEN.has(String(op.Status || '').trim().toLowerCase());

/** The op's own name: the op code title its Description starts with, else its short description. */
export function opName(op) {
  const desc = String(op?.Description || '').trim();
  if (desc) return desc.split(/\s*\(|\s+:|:\s/)[0].trim();
  return String(op?.Short_Description_DM || op?.Short_Description || '').trim();
}

const ENGINE_WORDS = /engine|inboard|outboard|stern ?drive|outdrive|generator|\bcyl/i;

/** Batteries: 'out' for a removal, 'in' for an install, null for other battery work (a replacement, a test). */
export function batteryDirection(op) {
  const name = opName(op);
  if (!/batter(y|ies)/i.test(name)) return null;
  if (/re-?install|install|put (back|in)/i.test(name)) return 'in';
  if (/remov|take out|pull/i.test(name)) return 'out';
  return null;
}

/** Which kind of seasonal work an op is (a WORK_KINDS key), or null. One kind per op. */
export function workKindOf(op) {
  if (!op) return null;
  const name = opName(op);
  if (/bottom paint|antifoul/i.test(name)) return 'paint';
  if (batteryDirection(op)) return 'batteries';
  if (/\banodes?\b|\bzincs?\b/i.test(name)) return 'anodes';
  if (isShrinkwrapInstall(op)) return 'shrinkwrap';
  if (/winteriz/i.test(name) && ENGINE_WORDS.test(name)) return 'winterize';
  if (/commission/i.test(name) && ENGINE_WORDS.test(name)) return 'commission';
  return null;
}

const clean = v => String(v ?? '').trim();
const keyOf = (...parts) => parts.map(p => clean(p).toLowerCase()).join('|');

/** The material most of the boat's anodes are (its anode lines, or the old Anode_List text until it has some), or ''. */
export const anodeMaterial = boat => mainMaterial(boat);

function engineGroup(op, boat) {
  const name = opName(op);
  if (/generator/i.test(name)) return { key: 'generator', label: 'Generator' };
  if (/outboard/i.test(name)) return { key: 'outboard', label: 'Outboard' };
  if (/stern ?drive|outdrive/i.test(name)) return { key: 'sterndrive', label: 'Sterndrive' };
  if (/inboard/i.test(name)) return { key: 'inboard', label: 'Inboard' };
  const type = clean(Object.values(boat?.Engines || {}).find(e => clean(e?.Engine_Type))?.Engine_Type);
  if (type) return { key: type.toLowerCase(), label: type };
  return { key: 'unknown', label: 'Engine type not on file' };
}

/**
 * The group an op falls in within its kind: { key, label }. Ops in one group share supplies or a setup
 * (one can of paint and one roller color; one anode material; one film width; one engine type).
 * boat: the boat record (Bottom_Paint_*, Anode_List, Engines, Boat_Length / Beam / Shrinkwrap_*).
 */
export function workGroupOf(kind, op, boat) {
  if (kind === 'paint') {
    const product = clean(boat?.Bottom_Paint_Product);
    if (!product) return { key: 'unknown', label: 'Paint not on file' };
    const type = clean(boat.Bottom_Paint_Type);
    const color = clean(boat.Bottom_Paint_Color);
    return { key: keyOf(product, type, color), label: `${product}${color ? `, ${color}` : ''}` };
  }
  if (kind === 'batteries') {
    const dir = batteryDirection(op);
    return dir === 'in' ? { key: 'in', label: 'Batteries in' } : { key: 'out', label: 'Batteries out' };
  }
  if (kind === 'anodes') {
    const m = anodeMaterial(boat);
    return m ? { key: m, label: m[0].toUpperCase() + m.slice(1) } : { key: 'unknown', label: 'Material not on file' };
  }
  if (kind === 'shrinkwrap') {
    const w = planWrap(boat || {});
    return w ? { key: String(w.width), label: `${w.width} ft film` } : { key: 'unknown', label: 'Size not known' };
  }
  if (kind === 'winterize' || kind === 'commission') return engineGroup(op, boat);
  return { key: 'other', label: 'Other' };
}

/** Boat_IDs (strings) with an open op of `kind`: what a Yard Map layer marks. */
export function boatIdsWithWork(kind, ops) {
  const ids = new Set();
  for (const op of ops || []) {
    if (op && op.Boat_ID != null && isOpenOp(op) && workKindOf(op) === kind) ids.add(String(op.Boat_ID));
  }
  return ids;
}

/**
 * The open ops of `kind`, grouped: [{ key, label, ops: [op...], boatIds: [id...] }], biggest group first.
 * boatOf(op) returns the op's boat record (or null). skip(op) true leaves an op out (one already in a run,
 * a boat that isn't on a yard map).
 */
export function workGroups(kind, ops, boatOf = () => null, skip = () => false) {
  const groups = new Map();
  for (const op of ops || []) {
    if (!op || op.Boat_ID == null || !isOpenOp(op) || workKindOf(op) !== kind || skip(op)) continue;
    const g = workGroupOf(kind, op, boatOf(op));
    const e = groups.get(g.key) || { key: g.key, label: g.label, ops: [], boatIds: [] };
    e.ops.push(op);
    const id = String(op.Boat_ID);
    if (!e.boatIds.includes(id)) e.boatIds.push(id);
    groups.set(g.key, e);
  }
  // Biggest first; the "not on file" group last, since it can't be worked as one.
  return [...groups.values()].sort((a, b) => (a.key === 'unknown') - (b.key === 'unknown')
    || b.boatIds.length - a.boatIds.length || a.label.localeCompare(b.label));
}

/**
 * Walking order for boats on one yard: rows from the gate (top of the yard's own coordinate space, as the
 * landmark line counts them), and a snake through them, so row 1 is walked left to right, row 2 back right
 * to left, and so on. spots: [{ id, x, y, h }] with x, y the boat's middle and h its drawn length.
 * Returns [{ id, row, n }] in that order (row from 1, n the walking number from 1).
 */
export function walkOrder(spots) {
  const list = (spots || []).filter(s => s && Number.isFinite(s.x) && Number.isFinite(s.y));
  if (!list.length) return [];
  const hs = list.map(s => Number(s.h) || 0).sort((a, b) => a - b);
  const tol = Math.max(30, hs[Math.floor(hs.length / 2)] * 0.6);
  const rows = [];
  for (const s of [...list].sort((a, b) => a.y - b.y)) {
    const row = rows.find(r => Math.abs(r.y - s.y) <= tol);
    if (row) { row.spots.push(s); row.y = row.spots.reduce((t, b) => t + b.y, 0) / row.spots.length; }
    else rows.push({ y: s.y, spots: [s] });
  }
  rows.sort((a, b) => a.y - b.y);
  const out = [];
  rows.forEach((r, i) => {
    r.spots.sort((a, b) => (i % 2 ? b.x - a.x : a.x - b.x));
    r.spots.forEach(s => out.push({ id: String(s.id), row: i + 1, n: out.length + 1 }));
  });
  return out;
}

// One color per group in Work mode, picked by the group's place in the list (badges and the rail swatch).
export const GROUP_COLORS = ['#D64545', '#1462B8', '#0F8C82', '#B8860B', '#7B4FB8', '#D9741A', '#2F7D32', '#C2408A'];
export const groupColor = i => GROUP_COLORS[((i % GROUP_COLORS.length) + GROUP_COLORS.length) % GROUP_COLORS.length];

// ─── Runs (session 3) ─────────────────────────────────────────────────────
// /workRuns/{id} = { kind, group, groupLabel, yard, areas: [{ points: [{x,y}] }], opIds: { opId: boatId },
//   order: [boatId...], date: 'YYYY-MM-DD', techs: { uid: name }, status: 'open' | 'done',
//   createdBy, createdAt, updatedBy, updatedAt }. The office writes it; each op in it gets the run's
// Techs (first names, ", ") and Schedule_Date, so the Scheduler, hours and Today stay right.

/** Is point {x,y} inside the polygon [{x,y}...] (even-odd rule)? */
export function pointInPolygon(pt, poly) {
  const p = poly || [];
  let inside = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const a = p[i], b = p[j];
    if ((a.y > pt.y) !== (b.y > pt.y) && pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** The ids of the spots [{ id, x, y }] whose middle is inside the drawn area. */
export const spotsInArea = (spots, poly) => (poly?.length >= 3 ? (spots || []).filter(s => pointInPolygon(s, poly)).map(s => String(s.id)) : []);

/** A box drawn from one corner to the other, as an area. */
export const boxArea = (a, b) => [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }];

/**
 * A run's order after boats are added or taken off: kept as it was (the office may have reordered it),
 * boats no longer in it dropped, new ones put in walking order after the rest.
 * walked: walkOrder() of every boat now in the run.
 */
export function mergeOrder(prev, walked) {
  const now = new Set((walked || []).map(w => w.id));
  const kept = (prev || []).map(String).filter(id => now.has(id));
  const seen = new Set(kept);
  return [...kept, ...(walked || []).map(w => w.id).filter(id => !seen.has(id))];
}

/** Move one boat in an order by delta (-1 up, +1 down). */
export function moveInOrder(order, id, delta) {
  const out = [...(order || [])];
  const i = out.indexOf(String(id));
  const j = i + delta;
  if (i < 0 || j < 0 || j >= out.length) return out;
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Estimated hours of the ops: { hours, missing } (missing = ops with no estimate). */
export function runHours(ops) {
  let hours = 0, missing = 0;
  for (const op of ops || []) {
    const h = parseFloat(op?.Estimated_Hours);
    if (h > 0) hours += h; else missing++;
  }
  return { hours: Math.round(hours * 100) / 100, missing };
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const round1 = n => Math.round(n * 10) / 10;

/**
 * What to take from the shop for a run: { lines: [{ label, qty, text }], notes: [...] } (notes say what
 * couldn't be counted). items: [{ op, boat }] (boat = the boat record). extra: { seasonRules (the
 * /seasonalOrdering rules, for paint coverage and can size), batteries: { boatId: count },
 * partOf(partId) (the inventory part, for anode lines linked to one) }.
 */
export function runSupplies(kind, items, extra = {}) {
  const lines = new Map();
  const notes = [];
  const add = (label, qty, more = {}) => {
    const e = lines.get(label) || { label, qty: 0, ...more };
    e.qty += qty;
    lines.set(label, e);
  };
  const list = items || [];
  if (kind === 'paint') {
    const R = normalizeRules(extra.seasonRules);
    let unknown = 0;
    for (const { boat } of list) {
      const product = clean(boat?.Bottom_Paint_Product);
      const row = product ? catalogRowFor(R.paint.catalog, product, boat.Bottom_Paint_Color) : null;
      const need = row ? paintNeed(boat, R, row) : null;
      if (!need) { unknown++; continue; }
      const color = clean(boat.Bottom_Paint_Color);
      add(`${product}${color ? `, ${color}` : ''}`, need.gallons, { can: row.unitGallons });
    }
    for (const e of lines.values()) {
      e.text = `${round1(e.qty)} gal${e.can ? ` (${Math.ceil(e.qty / e.can)} × ${e.can} gal)` : ''}`;
    }
    if (unknown) notes.push(`${plural(unknown, 'boat')} without a size or a paint coverage in Season order › Rules: not counted`);
  } else if (kind === 'batteries') {
    const counts = extra.batteries || {};
    let unknown = 0;
    for (const { boat, op } of list) {
      const n = Number(counts[String(boat?.Boat_ID ?? op?.Boat_ID)]) || 0;
      if (n) add('Batteries', n); else unknown++;
    }
    if (unknown) notes.push(`${plural(unknown, 'boat')} with no battery records: count them on the job`);
  } else if (kind === 'anodes') {
    let unknown = 0;
    for (const { boat } of list) {
      const lines = anodesOf(boat);
      if (!lines.length) unknown++;
      anodeNeeds(lines, extra.partOf || (() => null)).forEach(n => add(n.label, n.qty));
    }
    if (unknown) notes.push(`${plural(unknown, 'boat')} with no anode list`);
  } else if (kind === 'shrinkwrap') {
    let unknown = 0;
    for (const { boat } of list) {
      const w = planWrap(boat || {});
      if (w) add(`${w.width} ft film`, 1); else unknown++;
    }
    for (const e of lines.values()) e.text = plural(e.qty, 'boat');
    if (unknown) notes.push(`${plural(unknown, 'boat')} with no length or beam`);
  } else if (kind === 'winterize' || kind === 'commission') {
    for (const { boat, op } of list) {
      const engines = Object.keys(boat?.Engines || {}).length || 1;
      add(`${workGroupOf(kind, op, boat).label} engines`, engines);
    }
  }
  const out = [...lines.values()].map(e => ({ label: e.label, qty: round1(e.qty), text: e.text || String(round1(e.qty)) }));
  out.sort((a, b) => a.label.localeCompare(b.label));
  return { lines: out, notes };
}

/** The op's Techs text for a run's people: first names, ", " (the way the Scheduler stores them). */
export const techsText = techs => Object.values(techs || {}).filter(Boolean).map(n => String(n).split(' ')[0])
  .sort((a, b) => a.localeCompare(b)).join(', ');

/**
 * The database writes that make or change a run, as one multi-path update:
 *   each op now in it gets the run's Techs + Schedule_Date (+ Assigned_By / Assigned_At);
 *   each op taken off it goes back to the backlog, but only if it still has what the run gave it
 *   (someone may have rescheduled it since).
 * run: the record to save (opIds, techs, date). prev: the run as it was (null when new).
 * opsById: { opId: op } as this device has them.
 */
export function runWrites(runId, run, prev, opsById, by, at) {
  const w = {};
  const names = techsText(run.techs);
  for (const id of Object.keys(run.opIds || {})) {
    w[`data/${id}/Techs`] = names;
    w[`data/${id}/Schedule_Date`] = run.date;
    w[`data/${id}/Assigned_By`] = by;
    w[`data/${id}/Assigned_At`] = at;
  }
  if (prev) {
    const was = techsText(prev.techs);
    for (const id of Object.keys(prev.opIds || {})) {
      if (run.opIds?.[id]) continue;
      const op = opsById?.[id];
      if (op && (op.Techs || '') === was && String(op.Schedule_Date || '').slice(0, 10) === prev.date) {
        w[`data/${id}/Techs`] = '';
        w[`data/${id}/Schedule_Date`] = '';
      }
    }
  }
  w[`workRuns/${runId}`] = run;
  return w;
}

/** Breaking up a run: delete it and put back its ops that still have what the run gave them. */
export function breakUpWrites(runId, prev, opsById) {
  const w = runWrites(runId, { opIds: {}, techs: {}, date: '' }, prev, opsById, '', 0);
  w[`workRuns/${runId}`] = null;
  return w;
}

/** Ops of open runs, opId → runId, for leaving them out of the groups (except the run being edited). */
export function opsInRuns(runs, exceptId = null) {
  const out = {};
  for (const [id, r] of Object.entries(runs || {})) {
    if (!r || id === exceptId || r.status === 'done') continue;
    Object.keys(r.opIds || {}).forEach(op => { out[op] = id; });
  }
  return out;
}

/** A short name for a run: "Bottom paint: Micron CSC, Red · 1-11 · 9 boats". */
export function runTitle(run) {
  const n = Object.keys(run?.opIds || {}).length;
  const kind = KIND_LABEL[run?.kind] || 'Work';
  return [run?.groupLabel ? `${kind}: ${run.groupLabel}` : kind, run?.yard, plural(n, 'boat')].filter(Boolean).join(' · ');
}

// ─── The run in the techs app (session 4) ─────────────────────────────────

const DONE = new Set(['complete', 'ready to bill', 'billed', 'closed']);
/** Finished (the tech's Finish operation sets Complete or Ready to Bill). */
export const isDoneOp = op => !!op && DONE.has(String(op.Status || '').trim().toLowerCase());

/** Is this person on the run? By uid, or by first name the way ops store Techs. */
export function isOnRun(run, uid, firstName) {
  if (!run) return false;
  if (uid && run.techs?.[uid]) return true;
  const first = String(firstName || '').trim().split(' ')[0].toLowerCase();
  return !!first && Object.values(run.techs || {}).some(n => String(n || '').split(' ')[0].toLowerCase() === first);
}

/**
 * The run's stops in its order: [{ boatId, n, ops: [op...], done }] (done = every op there finished;
 * an op this device doesn't have, or that's gone, counts as nothing to do). opOf(id) → op or null.
 */
export function runStops(run, opOf) {
  const byBoat = new Map();
  Object.entries(run?.opIds || {}).forEach(([opId, boatId]) => {
    const op = opOf(opId);
    const list = byBoat.get(String(boatId)) || [];
    if (op) list.push(op);
    byBoat.set(String(boatId), list);
  });
  const order = (run?.order || []).map(String).filter(id => byBoat.has(id));
  [...byBoat.keys()].forEach(id => { if (!order.includes(id)) order.push(id); });
  return order.map((boatId, i) => {
    const ops = byBoat.get(boatId);
    return { boatId, n: i + 1, ops, done: ops.every(isDoneOp) };
  });
}

/** { stops, done, left, hoursLeft, next } for a run's card and header. */
export function runProgress(run, opOf) {
  const stops = runStops(run, opOf);
  const done = stops.filter(s => s.done).length;
  const hoursLeft = runHours(stops.flatMap(s => s.ops.filter(o => !isDoneOp(o)))).hours;
  return { stops, done, left: stops.length - done, hoursLeft, next: stops.find(s => !s.done) || null };
}

/**
 * The runs to show this person on Today: open, they're on it, its day has come (an unfinished one
 * from an earlier day stays), and something is left. Earliest day first.
 */
export function myRunsToday(runs, uid, firstName, today, opOf) {
  return Object.entries(runs || {})
    .filter(([, r]) => r && r.status !== 'done' && isOnRun(r, uid, firstName) && String(r.date || '') <= today)
    .map(([id, r]) => ({ id, run: r, progress: runProgress(r, opOf) }))
    .filter(x => x.progress.left > 0)
    .sort((a, b) => String(a.run.date).localeCompare(String(b.run.date)) || a.id.localeCompare(b.id));
}

/**
 * The part of the yard the run's small map shows: the run's boats with room around them, never smaller
 * than minSize (yard units) so a run of one boat still shows its neighbours. boats: [{ x, y, w, h }]
 * (top-left and size, as /maps/boats stores them).
 */
export function runMapBox(boats, pad = 40, minSize = 220) {
  const list = (boats || []).filter(b => b && Number.isFinite(b.x) && Number.isFinite(b.y));
  if (!list.length) return null;
  let x0 = Math.min(...list.map(b => b.x)), y0 = Math.min(...list.map(b => b.y));
  let x1 = Math.max(...list.map(b => b.x + (b.w || 0))), y1 = Math.max(...list.map(b => b.y + (b.h || 0)));
  x0 -= pad; y0 -= pad; x1 += pad; y1 += pad;
  const grow = (a, b) => { const d = Math.max(0, minSize - (b - a)) / 2; return [a - d, b + d]; };
  [x0, x1] = grow(x0, x1);
  [y0, y1] = grow(y0, y1);
  const r = n => Math.round(n * 10) / 10;
  return { x: r(x0), y: r(y0), w: r(x1 - x0), h: r(y1 - y0) };
}

/** box grown to the shape of a W×H map (same center), so a boat at its edge isn't cut off. */
export function fitView(box, W, H) {
  const shape = W / H;
  if (box.w / box.h < shape) {
    const w = box.h * shape;
    return { x: box.x - (w - box.w) / 2, y: box.y, w, h: box.h };
  }
  const h = box.w / shape;
  return { x: box.x, y: box.y - (h - box.h) / 2, w: box.w, h };
}

/**
 * The view kept on the map: no wider than `world` (already fitted to the map's shape), no closer than
 * minW across, its shape unchanged. A view wider than the world centers on it; otherwise it can't leave.
 */
export function clampView(view, world, minW) {
  const shape = view.h / view.w;
  const maxW = Math.max(world.w, world.h / shape);
  const w = Math.min(maxW, Math.max(Math.min(minW, maxW), view.w));
  const h = w * shape;
  const place = (center, len, lo, span) => (len >= span ? lo + span / 2 : Math.min(lo + span - len / 2, Math.max(lo + len / 2, center)));
  return {
    x: place(view.x + view.w / 2, w, world.x, world.w) - w / 2,
    y: place(view.y + view.h / 2, h, world.y, world.h) - h / 2,
    w, h,
  };
}

/** Zoomed by factor (<1 is closer) about a point given as fractions of the view; the point stays put, even at the limits. */
export function zoomView(view, factor, fx, fy, world, minW) {
  const maxW = Math.max(world.w, world.h / (view.h / view.w));
  const w = Math.min(maxW, Math.max(Math.min(minW, maxW), view.w * factor));
  const h = w * (view.h / view.w);
  return { x: view.x + (view.w - w) * fx, y: view.y + (view.h - h) * fy, w, h };
}

/**
 * A walking order for boats from where someone stands: always the nearest next, then uncrossed (a line
 * that crosses itself is never the shortest). points: [{ id, x, y }]; start: { x, y }. Returns the ids.
 */
export function sortFromHere(points, start) {
  const left = (points || []).filter(p => p && Number.isFinite(p.x) && Number.isFinite(p.y));
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const path = [];
  let at = start;
  while (left.length) {
    let best = 0;
    left.forEach((p, i) => { if (dist(at, p) < dist(at, left[best])) best = i; });
    at = left.splice(best, 1)[0];
    path.push(at);
  }
  const pointAt = i => (i < 0 ? start : path[i]);
  for (let improved = true, rounds = 0; improved && rounds < 20; rounds++) {
    improved = false;
    for (let i = 0; i < path.length - 1; i++) {
      for (let j = i + 1; j < path.length; j++) {
        // Flipping i..j swaps the leg into i and the leg out of j; the last stop has no leg out.
        const after = path[j + 1];
        const before = dist(pointAt(i - 1), path[i]) + (after ? dist(path[j], after) : 0);
        const flipped = dist(pointAt(i - 1), path[j]) + (after ? dist(path[i], after) : 0);
        if (flipped < before - 1e-9) {
          path.splice(i, j - i + 1, ...path.slice(i, j + 1).reverse());
          improved = true;
        }
      }
    }
  }
  return path.map(p => p.id);
}

/** Marks the run finished: what the techs app writes when the last stop is done. */
export const runDoneWrites = (runId, by, at) => ({ [`workRuns/${runId}/status`]: 'done', [`workRuns/${runId}/doneAt`]: at, [`workRuns/${runId}/doneBy`]: by });

// ─── The office's overview (session 7): Scheduler › Tasks and the Dashboard ──

/**
 * Runs by where they stand on `today`: { today, behind (an earlier day, not finished), upcoming (later
 * days), done (finished, any day) }, each [{ id, run, progress }] earliest first. opOf(opId) → the op.
 */
export function runsOverview(runs, opOf, today) {
  const out = { today: [], behind: [], upcoming: [], done: [] };
  Object.entries(runs || {}).forEach(([id, run]) => {
    if (!run || !run.date) return;
    const progress = runProgress(run, opOf);
    const finished = run.status === 'done' || (progress.stops.length > 0 && progress.left === 0);
    const item = { id, run, progress };
    if (finished) out.done.push(item);
    else if (run.date === today) out.today.push(item);
    else if (run.date < today) out.behind.push(item);
    else out.upcoming.push(item);
  });
  Object.values(out).forEach(list => list.sort((a, b) => String(a.run.date).localeCompare(String(b.run.date)) || a.id.localeCompare(b.id)));
  return out;
}

/** The runs on one day, earliest made first: [{ id, run }]. */
export const runsOnDay = (runs, date) => Object.entries(runs || {}).filter(([, r]) => r && r.date === date)
  .sort(([a, x], [b, y]) => (x.createdAt || 0) - (y.createdAt || 0) || a.localeCompare(b)).map(([id, run]) => ({ id, run }));

/** "Paint" / "Batteries" / … : a kind's short name for a small chip. */
export const KIND_SHORT = { paint: 'Paint', batteries: 'Batteries', anodes: 'Anodes', shrinkwrap: 'Shrinkwrap', winterize: 'Winterize', commission: 'Commission' };
